import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Filter,
  StickyNote,
  Calendar,
  Infinity,
  Send,
  Users,
  ArrowLeftRight,
  Building2,
  ChevronDown,
  Check,
  DollarSign,
  History,
  FolderOpen,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import EmployeeModal from "../components/EmployeeModal";
import BulkRateUpdateModal from "../components/BulkRateUpdateModal";
import "../styles/Employees.css";

const Employees = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBulkRateModal, setShowBulkRateModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const { hasPermission, user } = useAuth();

  // Notes state
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteType, setNewNoteType] = useState("monthly");
  const [showAllMonths, setShowAllMonths] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState("active"); // Default sort by active status
  const [sortDirection, setSortDirection] = useState("desc"); // Active first

  // Autocomplete suggestions
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [customerNameSuggestions, setCustomerNameSuggestions] = useState([]);
  const [siteNameSuggestions, setSiteNameSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [showSiteSuggestions, setShowSiteSuggestions] = useState(false);

  // Filter states - arrays for multi-select
  const [filters, setFilters] = useState({
    name: "",
    employeeId: "",
    customerId: "",
    customerNames: [],
    paymentMethod: "",
    types: [],
    siteName: "",
  });

  // Editing note state
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");

  // Multi-select dropdown states
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [employees, filters, sortField, sortDirection]);

  // Handle search parameter from URL
  useEffect(() => {
    const searchParam = searchParams.get("search");
    if (searchParam) {
      setFilters((prev) => ({
        ...prev,
        employeeId: searchParam,
      }));
      // Clear the search param after setting it
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEmployees({
        limit: 1000,
        include_customer: true,
        include_inactive: true,
      });

      let employeesList = response.employees || [];

      // Filter employees for site_admin users based on assigned customers
      if (user?.permission === "site_admin" && user?.assigned_customers) {
        employeesList = employeesList.filter((emp) =>
          user.assigned_customers.includes(emp.customer_id),
        );
      }

      setEmployees(employeesList);
    } catch (error) {
      console.error("Failed to load employees:", error);
      alert("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  // Check if employee was created within last 30 days
  const isNewEmployee = (createdAt) => {
    if (!createdAt) return false;
    const createdDate = new Date(createdAt);
    const now = new Date();
    const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);
    return daysDiff <= 30;
  };

  const applyFilters = () => {
    let filtered = [...employees];

    // Filter by name (partial match for text input)
    if (filters.name) {
      filtered = filtered.filter((emp) =>
        emp.employee_name?.toLowerCase().includes(filters.name.toLowerCase()),
      );
    }

    // Filter by employee ID (exact match for dropdown)
    if (filters.employeeId) {
      filtered = filtered.filter(
        (emp) => emp.employee_id?.toString() === filters.employeeId.toString(),
      );
    }

    // Filter by customer ID (exact match for dropdown)
    if (filters.customerId) {
      filtered = filtered.filter(
        (emp) => emp.customer_id?.toString() === filters.customerId.toString(),
      );
    }

    // Filter by customer names (multi-select)
    if (filters.customerNames.length > 0) {
      filtered = filtered.filter((emp) =>
        filters.customerNames.includes(emp.customer?.customer_name),
      );
    }

    // Filter by payment method
    if (filters.paymentMethod) {
      filtered = filtered.filter(
        (emp) => emp.payment_method === filters.paymentMethod,
      );
    }

    // Filter by types (multi-select)
    if (filters.types.length > 0) {
      filtered = filtered.filter((emp) => filters.types.includes(emp.type));
    }

    // Filter by site name (text search with contains)
    if (filters.siteName) {
      filtered = filtered.filter((emp) =>
        emp.customer?.site_name
          ?.toLowerCase()
          .includes(filters.siteName.toLowerCase()),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "active":
          aValue = a.active ? 1 : 0;
          bValue = b.active ? 1 : 0;
          break;
        case "id":
          aValue = a.employee_id;
          bValue = b.employee_id;
          break;
        case "name":
          aValue = (a.employee_name || "").toLowerCase();
          bValue = (b.employee_name || "").toLowerCase();
          break;
        case "customer":
          aValue = (a.customer?.customer_name || "").toLowerCase();
          bValue = (b.customer?.customer_name || "").toLowerCase();
          break;
        case "type":
          aValue = a.type || "";
          bValue = b.type || "";
          break;
        case "payment":
          aValue = a.payment_method || "";
          bValue = b.payment_method || "";
          break;
        case "rate":
          aValue = a.hour_rate || 0;
          bValue = b.hour_rate || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredEmployees(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));

    // Generate autocomplete suggestions
    if (key === "name" && value.length > 0) {
      const suggestions = [
        ...new Set(
          employees
            .filter((emp) =>
              emp.employee_name?.toLowerCase().includes(value.toLowerCase()),
            )
            .map((emp) => emp.employee_name)
            .slice(0, 10),
        ),
      ];
      setNameSuggestions(suggestions);
      setShowNameSuggestions(true);
    } else if (key === "name") {
      setShowNameSuggestions(false);
    }

    if (key === "customerName" && value.length > 0) {
      const suggestions = [
        ...new Set(
          employees
            .filter((emp) =>
              emp.customer?.customer_name
                ?.toLowerCase()
                .includes(value.toLowerCase()),
            )
            .map((emp) => emp.customer?.customer_name)
            .filter(Boolean)
            .slice(0, 10),
        ),
      ];
      setCustomerNameSuggestions(suggestions);
      setShowCustomerSuggestions(true);
    } else if (key === "customerName") {
      setShowCustomerSuggestions(false);
    }

    // Site name autocomplete
    if (key === "siteName" && value.length > 0) {
      const suggestions = [
        ...new Set(
          employees
            .filter((emp) =>
              emp.customer?.site_name
                ?.toLowerCase()
                .includes(value.toLowerCase()),
            )
            .map((emp) => emp.customer?.site_name)
            .filter(Boolean)
            .slice(0, 10),
        ),
      ];
      setSiteNameSuggestions(suggestions);
      setShowSiteSuggestions(true);
    } else if (key === "siteName") {
      setShowSiteSuggestions(false);
    }
  };

  const selectSuggestion = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setShowNameSuggestions(false);
    setShowCustomerSuggestions(false);
    setShowSiteSuggestions(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "⇅";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const clearFilters = () => {
    setFilters({
      name: "",
      employeeId: "",
      customerId: "",
      customerNames: [],
      paymentMethod: "",
      types: [],
      siteName: "",
    });
  };

  // Multi-select toggle handler
  const toggleMultiFilter = (filterKey, value) => {
    setFilters((prev) => {
      const current = prev[filterKey];
      if (current.includes(value)) {
        return { ...prev, [filterKey]: current.filter((v) => v !== value) };
      } else {
        return { ...prev, [filterKey]: [...current, value] };
      }
    });
  };

  // Note editing functions
  const handleEditNote = (note) => {
    setEditingNote(note.note_id);
    setEditNoteText(note.note_text);
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteText.trim()) return;

    try {
      await apiService.updateNote(noteId, { note_text: editNoteText });
      setEditingNote(null);
      setEditNoteText("");
      loadNotes(selectedEmployee.employee_id);
    } catch (error) {
      console.error("Failed to update note:", error);
      alert("עדכון ההערה נכשל");
    }
  };

  const cancelEditNote = () => {
    setEditingNote(null);
    setEditNoteText("");
  };

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setShowModal(true);
  };

  const handleRowClick = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailModal(true);
    loadNotes(employee.employee_id);
  };

  const handleEditEmployee = (employee, e) => {
    e.stopPropagation(); // Prevent row click
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const handleDeleteEmployee = async (employeeId, e) => {
    e.stopPropagation(); // Prevent row click

    if (!hasPermission("admin")) {
      alert("אין לך הרשאה להשבית עובדים");
      return;
    }

    if (
      !window.confirm(
        "האם אתה בטוח שברצונך להשבית עובד זה? פעולה זו תשנה את הסטטוס ללא פעיל.",
      )
    ) {
      return;
    }

    try {
      await apiService.deleteEmployee(employeeId);
      alert("העובד הושבת בהצלחה");
      loadEmployees();
    } catch (error) {
      console.error("Failed to deactivate employee:", error);
      alert("השבתת העובד נכשלה");
    }
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    setSelectedEmployee(null);
    if (shouldReload) {
      loadEmployees();
    }
  };

  // Notes functions
  const loadNotes = async (employeeId) => {
    try {
      setLoadingNotes(true);
      const response = await apiService.getEmployeeNotes(
        employeeId,
        showAllMonths,
      );
      setNotes(response.notes || []);
    } catch (error) {
      console.error("Failed to load notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim() || !selectedEmployee) return;

    try {
      await apiService.createEmployeeNote(selectedEmployee.employee_id, {
        note_text: newNoteText,
        note_type: newNoteType,
      });
      setNewNoteText("");
      loadNotes(selectedEmployee.employee_id);
    } catch (error) {
      console.error("Failed to add note:", error);
      alert("הוספת ההערה נכשלה");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק הערה זו?")) return;

    try {
      await apiService.deleteNote(noteId);
      loadNotes(selectedEmployee.employee_id);
    } catch (error) {
      console.error("Failed to delete note:", error);
      alert("מחיקת ההערה נכשלה");
    }
  };

  const activeFiltersCount = Object.values(filters).filter((v) =>
    Array.isArray(v) ? v.length > 0 : v !== "",
  ).length;

  return (
    <div className='employees-page'>
      <div className='page-header'>
        <div>
          <h1>עובדים</h1>
          <p>ניהול רשומות ומידע עובדים</p>
        </div>
        <div className='header-buttons'>
          {hasPermission("admin") && (
            <button
              className='btn-secondary'
              onClick={() => navigate("/employees/missing-rates")}
            >
              <DollarSign size={18} />
              עובדים ללא תעריפים
            </button>
          )}
          {hasPermission("admin") && (
            <button
              className='btn-secondary btn-bulk-rates'
              onClick={() => setShowBulkRateModal(true)}
            >
              <DollarSign size={18} />
              עדכון תעריפים מרוכז
            </button>
          )}
          {(hasPermission("editor") || hasPermission("admin")) && (
            <button className='btn-primary' onClick={handleAddEmployee}>
              <Plus size={18} />
              הוסף עובד
            </button>
          )}
        </div>
      </div>

      {/* Employee Statistics */}
      <h3 className='stats-section-title'>סטטיסטיקות עובדים</h3>
      <div className='employee-stats'>
        <div className='stat-card'>
          <div className='stat-icon total'>
            <Users size={24} />
          </div>
          <span className='stat-icon-title'>כולם</span>
          <div className='stat-info'>
            <span className='stat-value'>{filteredEmployees.length}</span>
            <span className='stat-label'>סה"כ עובדים</span>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon transfer'>
            <ArrowLeftRight size={24} />
          </div>
          <span className='stat-icon-title'>קבלן</span>
          <div className='stat-info'>
            <span className='stat-value'>
              {filteredEmployees.filter((emp) => emp.type === "קבלן").length}
            </span>
            <span className='stat-label'>עובדי קבלן</span>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon company'>
            <Building2 size={24} />
          </div>
          <span className='stat-icon-title'>חברה</span>
          <div className='stat-info'>
            <span className='stat-value'>
              {filteredEmployees.filter((emp) => emp.type === "חברה").length}
            </span>
            <span className='stat-label'>עובדי חברה</span>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon mixed'>
            <Users size={24} />
          </div>
          <span className='stat-icon-title'>משולב</span>
          <div className='stat-info'>
            <span className='stat-value'>
              {
                filteredEmployees.filter((emp) => emp.type === "קבלן ו חברה")
                  .length
              }
            </span>
            <span className='stat-label'>קבלן ו חברה</span>
          </div>
        </div>
      </div>

      <div className='filters-section'>
        <div className='filters-header'>
          <button
            className={`btn-filter ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            מסננים {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </button>
          {activeFiltersCount > 0 && (
            <button className='btn-clear' onClick={clearFilters}>
              <X size={16} />
              נקה הכל
            </button>
          )}
        </div>

        {showFilters && (
          <div className={`filters-grid ${showFilters ? "open" : ""}`}>
            <div className='filter-field autocomplete-field'>
              <label>שם עובד</label>
              <input
                type='text'
                placeholder='חפש לפי שם...'
                value={filters.name}
                onChange={(e) => handleFilterChange("name", e.target.value)}
                onFocus={() => filters.name && setShowNameSuggestions(true)}
                onBlur={() =>
                  setTimeout(() => setShowNameSuggestions(false), 200)
                }
              />
              {showNameSuggestions && nameSuggestions.length > 0 && (
                <div className='autocomplete-dropdown'>
                  {nameSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className='autocomplete-item'
                      onClick={() => selectSuggestion("name", suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className='filter-field'>
              <label>מספר עובד</label>
              <select
                value={filters.employeeId}
                onChange={(e) =>
                  handleFilterChange("employeeId", e.target.value)
                }
              >
                <option value=''>כל המספרים</option>
                {[
                  ...new Set(
                    employees.map((emp) => emp.employee_id).filter(Boolean),
                  ),
                ]
                  .sort((a, b) => a - b)
                  .map((id, index) => (
                    <option key={index} value={id}>
                      {id}
                    </option>
                  ))}
              </select>
            </div>

            <div className='filter-field'>
              <label>
                שם לקוח{" "}
                {filters.customerNames.length > 0 && (
                  <span className='filter-count'>
                    ({filters.customerNames.length})
                  </span>
                )}
              </label>
              <select
                value=''
                onChange={(e) => {
                  if (e.target.value)
                    toggleMultiFilter("customerNames", e.target.value);
                }}
              >
                <option value=''>בחר לקוחות...</option>
                {[
                  ...new Set(
                    employees
                      .map((emp) => emp.customer?.customer_name)
                      .filter(Boolean),
                  ),
                ]
                  .sort()
                  .filter((name) => !filters.customerNames.includes(name))
                  .map((name, index) => (
                    <option key={index} value={name}>
                      {name}
                    </option>
                  ))}
              </select>
              {filters.customerNames.length > 0 && (
                <div className='selected-chips'>
                  {filters.customerNames.map((name, index) => (
                    <span key={index} className='chip'>
                      {name}
                      <X
                        size={14}
                        onClick={() => toggleMultiFilter("customerNames", name)}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className='filter-field'>
              <label>אמצעי תשלום</label>
              <select
                value={filters.paymentMethod}
                onChange={(e) =>
                  handleFilterChange("paymentMethod", e.target.value)
                }
              >
                <option value=''>כל אמצעי התשלום</option>
                <option value='העברה בנקאית'>העברה בנקאית</option>
                <option value='קבלן'>קבלן</option>
                <option value='צ׳ק'>צ׳ק</option>
              </select>
            </div>

            <div className='filter-field'>
              <label>
                סוג עובד{" "}
                {filters.types.length > 0 && (
                  <span className='filter-count'>({filters.types.length})</span>
                )}
              </label>
              <select
                value=''
                onChange={(e) => {
                  if (e.target.value)
                    toggleMultiFilter("types", e.target.value);
                }}
              >
                <option value=''>בחר סוג...</option>
                {["קבלן", "חברה", "קבלן ו חברה"]
                  .filter((type) => !filters.types.includes(type))
                  .map((type, index) => (
                    <option key={index} value={type}>
                      {type}
                    </option>
                  ))}
              </select>
              {filters.types.length > 0 && (
                <div className='selected-chips'>
                  {filters.types.map((type, index) => (
                    <span key={index} className='chip'>
                      {type}
                      <X
                        size={14}
                        onClick={() => toggleMultiFilter("types", type)}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className='filter-field autocomplete-field'>
              <label>שם אתר</label>
              <input
                type='text'
                placeholder='חפש לפי שם אתר...'
                value={filters.siteName}
                onChange={(e) => handleFilterChange("siteName", e.target.value)}
                onFocus={() => filters.siteName && setShowSiteSuggestions(true)}
                onBlur={() =>
                  setTimeout(() => setShowSiteSuggestions(false), 200)
                }
              />
              {showSiteSuggestions && siteNameSuggestions.length > 0 && (
                <div className='autocomplete-dropdown'>
                  {siteNameSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className='autocomplete-item'
                      onClick={() => selectSuggestion("siteName", suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className='results-info'>
        <p>
          מציג {filteredEmployees.length} מתוך {employees.length} עובדים
        </p>
      </div>

      {loading ? (
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>טוען עובדים...</p>
        </div>
      ) : (
        <div className='employees-table-container'>
          <table className='employees-table'>
            <thead>
              <tr>
                <th className='sortable' onClick={() => handleSort("id")}>
                  מספר {getSortIcon("id")}
                </th>
                <th className='sortable' onClick={() => handleSort("name")}>
                  שם {getSortIcon("name")}
                </th>
                <th className='sortable' onClick={() => handleSort("customer")}>
                  לקוח {getSortIcon("customer")}
                </th>
                <th className='sortable' onClick={() => handleSort("type")}>
                  סוג {getSortIcon("type")}
                </th>
                <th className='sortable' onClick={() => handleSort("payment")}>
                  אמצעי תשלום {getSortIcon("payment")}
                </th>
                <th className='sortable' onClick={() => handleSort("rate")}>
                  תעריף {getSortIcon("rate")}
                </th>
                <th className='sortable' onClick={() => handleSort("active")}>
                  סטטוס {getSortIcon("active")}
                </th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan='8' className='no-data'>
                    לא נמצאו עובדים
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((employee) => (
                  <tr
                    key={employee.employee_id}
                    onClick={() => handleRowClick(employee)}
                    className={`clickable-row ${!employee.active ? "inactive-row" : ""}`}
                  >
                    <td>{employee.employee_id}</td>
                    <td className='employee-name'>
                      {employee.employee_name}
                      {isNewEmployee(employee.created_at) && (
                        <span className='new-badge'>חדש</span>
                      )}
                      {employee.copied_from_partnership_id && (
                        <span className='koral-badge'>koral</span>
                      )}
                    </td>
                    <td>
                      {employee.customers && employee.customers.length > 0 ? (
                        <div className='customer-info multi-site'>
                          {employee.customers.map((cust, idx) => (
                            <div
                              key={cust.customer_id}
                              className={`customer-row ${cust.is_primary ? "primary" : "secondary"}`}
                            >
                              <span className='customer-name'>
                                {cust.customer_name}
                                {cust.is_primary && (
                                  <span className='primary-badge'>ראשי</span>
                                )}
                              </span>
                              <span className='customer-site'>
                                {cust.site_name || "אין אתר"}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : employee.customer ? (
                        <div className='customer-info'>
                          <span className='customer-name'>
                            {employee.customer.customer_name}
                          </span>
                          <span className='customer-site'>
                            {employee.customer.site_name || "אין אתר"}
                          </span>
                        </div>
                      ) : (
                        <span className='no-customer'>אין לקוח</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge badge-type badge-${employee.type}`}
                      >
                        {employee.type || "N/A"}
                      </span>
                    </td>
                    <td
                      className='payment-cell'
                      onMouseEnter={(e) => {
                        const tooltip =
                          e.currentTarget.querySelector(".bank-tooltip");
                        if (tooltip) {
                          const badge =
                            e.currentTarget.querySelector(".badge-payment");
                          if (badge) {
                            const rect = badge.getBoundingClientRect();
                            tooltip.style.top = `${
                              rect.top - tooltip.offsetHeight - 10
                            }px`;
                            tooltip.style.left = `${
                              rect.left +
                              rect.width / 2 -
                              tooltip.offsetWidth / 2
                            }px`;
                          }
                        }
                      }}
                    >
                      <span
                        className={`badge badge-payment badge-${
                          employee.payment_method
                        } ${employee.michpal_id ? "clickable-badge" : ""}`}
                        onClick={(e) => {
                          if (employee.michpal_id) {
                            e.stopPropagation();
                            navigator.clipboard
                              .writeText(employee.michpal_id.toString())
                              .then(() => {
                                // Show success feedback
                                const badge = e.currentTarget;
                                const originalText = badge.textContent;
                                badge.textContent = "✓ הועתק!";
                                badge.style.background = "#10b981";
                                badge.style.color = "white";
                                setTimeout(() => {
                                  badge.textContent = originalText;
                                  badge.style.background = "";
                                  badge.style.color = "";
                                }, 1500);
                              })
                              .catch((err) => {
                                console.error("Failed to copy:", err);
                              });
                          }
                        }}
                        title={
                          employee.michpal_id
                            ? `לחץ להעתקת מספר מיכפל: ${employee.michpal_id}`
                            : ""
                        }
                      >
                        {employee.payment_method === "העברה בנקאית"
                          ? "העברה בנקאית"
                          : employee.payment_method === "צ׳ק"
                            ? "צ׳ק"
                            : employee.payment_method || "N/A"}
                      </span>
                      {employee.payment_method === "העברה בנקאית" && (
                        <div className='bank-tooltip'>
                          {employee.account_name ||
                          employee.bank ||
                          employee.account ||
                          employee.branch ||
                          employee.michpal_id ? (
                            <>
                              {employee.account_name && (
                                <div className='bank-tooltip-row'>
                                  <span className='bank-tooltip-label'>
                                    שם בעל החשבון:
                                  </span>
                                  <span className='bank-tooltip-value'>
                                    {employee.account_name}
                                  </span>
                                </div>
                              )}
                              {employee.bank && (
                                <div className='bank-tooltip-row'>
                                  <span className='bank-tooltip-label'>
                                    בנק:
                                  </span>
                                  <span className='bank-tooltip-value'>
                                    {employee.bank}
                                  </span>
                                </div>
                              )}
                              {employee.account && (
                                <div className='bank-tooltip-row'>
                                  <span className='bank-tooltip-label'>
                                    מספר חשבון:
                                  </span>
                                  <span className='bank-tooltip-value'>
                                    {employee.account}
                                  </span>
                                </div>
                              )}
                              {employee.branch && (
                                <div className='bank-tooltip-row'>
                                  <span className='bank-tooltip-label'>
                                    סניף:
                                  </span>
                                  <span className='bank-tooltip-value'>
                                    {employee.branch}
                                  </span>
                                </div>
                              )}
                              {employee.michpal_id && (
                                <div className='bank-tooltip-row'>
                                  <span className='bank-tooltip-label'>
                                    מספר מיכפל:
                                  </span>
                                  <span className='bank-tooltip-value'>
                                    {employee.michpal_id}
                                  </span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className='bank-tooltip-row'>
                              <span className='bank-tooltip-value'>
                                אין מידע בנקאי זמין
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {employee.payment_method === "צ׳ק" &&
                        employee.michpal_id && (
                          <div className='bank-tooltip'>
                            <div className='bank-tooltip-row'>
                              <span className='bank-tooltip-label'>
                                מספר מיכפל:
                              </span>
                              <span className='bank-tooltip-value'>
                                {employee.michpal_id}
                              </span>
                            </div>
                          </div>
                        )}
                    </td>
                    <td>₪{employee.hour_rate || 0}</td>
                    <td>
                      <span
                        className={`badge ${
                          employee.active ? "badge-active" : "badge-inactive"
                        }`}
                      >
                        {employee.active ? "פעיל" : "לא פעיל"}
                      </span>
                    </td>
                    <td>
                      <div className='action-buttons'>
                        <button
                          className='btn-icon btn-history'
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/employees/${employee.employee_id}/history`,
                            );
                          }}
                          title='היסטוריית משכורות'
                        >
                          <History size={16} />
                        </button>
                        <button
                          className='btn-icon'
                          style={{ color: "#10b981" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/employees/${employee.employee_id}/profile`,
                            );
                          }}
                          title='פרופיל וקבצים'
                        >
                          <FolderOpen size={16} />
                        </button>
                        {(hasPermission("editor") ||
                          hasPermission("admin")) && (
                          <button
                            className='btn-icon btn-edit'
                            onClick={(e) => handleEditEmployee(employee, e)}
                            title='ערוך'
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        {hasPermission("admin") && (
                          <button
                            className='btn-icon btn-delete'
                            onClick={(e) =>
                              handleDeleteEmployee(employee.employee_id, e)
                            }
                            title='מחק'
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <EmployeeModal employee={selectedEmployee} onClose={handleModalClose} />
      )}

      {showBulkRateModal && (
        <BulkRateUpdateModal
          employees={employees}
          onClose={() => setShowBulkRateModal(false)}
          onSuccess={() => loadEmployees()}
        />
      )}

      {showDetailModal && selectedEmployee && (
        <div
          className='modal-overlay'
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className='modal-content detail-modal'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h2>פרטי עובד</h2>
              <div className='modal-header-actions'>
                {(hasPermission("editor") || hasPermission("admin")) && (
                  <button
                    className='btn-primary btn-edit-detail'
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowModal(true);
                    }}
                  >
                    <Edit2 size={18} />
                    ערוך עובד
                  </button>
                )}
                <button
                  className='modal-close'
                  onClick={() => setShowDetailModal(false)}
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className='detail-content'>
              <div className='detail-section'>
                <h3>מידע בסיסי</h3>
                <div className='detail-grid'>
                  <div className='detail-item'>
                    <span className='detail-label'>מספר עובד:</span>
                    <span className='detail-value'>
                      {selectedEmployee.employee_id}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>מספר עובד מערכת:</span>
                    <span className='detail-value'>
                      {selectedEmployee.employee_number || "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>שם:</span>
                    <span className='detail-value'>
                      {selectedEmployee.employee_name}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>סוג:</span>
                    <span className='detail-value'>
                      <span
                        className={`badge badge-type badge-${selectedEmployee.type}`}
                      >
                        {selectedEmployee.type || "N/A"}
                      </span>
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>סטטוס:</span>
                    <span className='detail-value'>
                      <span
                        className={`badge ${
                          selectedEmployee.active
                            ? "badge-active"
                            : "badge-inactive"
                        }`}
                      >
                        {selectedEmployee.active ? "פעיל" : "לא פעיל"}
                      </span>
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>קבלן:</span>
                    <span className='detail-value'>
                      {selectedEmployee.contractor || "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>מגייס:</span>
                    <span className='detail-value'>
                      {selectedEmployee.recruiter || "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>טלפון:</span>
                    <span className='detail-value'>
                      {selectedEmployee.phone_number || "---"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>כתובת:</span>
                    <span className='detail-value'>
                      {selectedEmployee.address || "---"}
                    </span>
                  </div>
                </div>
              </div>

              <div className='detail-section'>
                <h3>מידע לקוח</h3>
                <div className='detail-grid'>
                  <div className='detail-item'>
                    <span className='detail-label'>מספר לקוח:</span>
                    <span className='detail-value'>
                      {selectedEmployee.customer_id || "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>שם לקוח:</span>
                    <span className='detail-value'>
                      {selectedEmployee.customer?.customer_name || "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>שם אתר:</span>
                    <span className='detail-value'>
                      {selectedEmployee.customer?.site_name || "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>קוד לקוח:</span>
                    <span className='detail-value'>
                      {selectedEmployee.customer?.customer_code || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className='detail-section'>
                <h3>מידע תשלום</h3>
                <div className='detail-grid'>
                  <div className='detail-item'>
                    <span className='detail-label'>אמצעי תשלום:</span>
                    <span className='detail-value'>
                      <span
                        className={`badge badge-payment badge-${selectedEmployee.payment_method}`}
                      >
                        {selectedEmployee.payment_method || "N/A"}
                      </span>
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>תעריף שעתי:</span>
                    <span className='detail-value'>
                      ₪{selectedEmployee.hour_rate || 0}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>תעריף שעתי סופ"ש:</span>
                    <span className='detail-value'>
                      ₪{selectedEmployee.weekend_hour_rate || 0}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>מספר מיכפל:</span>
                    <span className='detail-value'>
                      {selectedEmployee.michpal_id || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Hide bank details for קבלן type */}
              {selectedEmployee.type !== "קבלן" && (
                <div className='detail-section'>
                  <h3>פרטי בנק</h3>
                  <div className='detail-grid'>
                    <div className='detail-item'>
                      <span className='detail-label'>בנק:</span>
                      <span className='detail-value'>
                        {selectedEmployee.bank || "N/A"}
                      </span>
                    </div>
                    <div className='detail-item'>
                      <span className='detail-label'>מספר חשבון:</span>
                      <span className='detail-value'>
                        {selectedEmployee.account || "N/A"}
                      </span>
                    </div>
                    <div className='detail-item'>
                      <span className='detail-label'>סניף:</span>
                      <span className='detail-value'>
                        {selectedEmployee.branch || "N/A"}
                      </span>
                    </div>
                    <div className='detail-item'>
                      <span className='detail-label'>שם בעל החשבון:</span>
                      <span className='detail-value'>
                        {selectedEmployee.account_name || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className='detail-section'>
                <h3>מידע מעסיק</h3>
                <div className='detail-grid'>
                  <div className='detail-item'>
                    <span className='detail-label'>קוד מעסיק:</span>
                    <span className='detail-value'>
                      {selectedEmployee.employer_code || "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>שם מעסיק:</span>
                    <span className='detail-value'>
                      {selectedEmployee.employer_name || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className='detail-section'>
                <h3>חותמות זמן</h3>
                <div className='detail-grid'>
                  <div className='detail-item'>
                    <span className='detail-label'>נוצר בתאריך:</span>
                    <span className='detail-value'>
                      {selectedEmployee.created_at
                        ? new Date(selectedEmployee.created_at).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-label'>עודכן בתאריך:</span>
                    <span className='detail-value'>
                      {selectedEmployee.updated_at
                        ? new Date(selectedEmployee.updated_at).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className='notes-section'>
                <div className='notes-header'>
                  <h3>
                    <StickyNote size={20} />
                    הערות
                  </h3>
                  <button
                    className='btn-toggle-months'
                    onClick={() => {
                      setShowAllMonths(!showAllMonths);
                      loadNotes(selectedEmployee.employee_id);
                    }}
                  >
                    {showAllMonths ? "הצג חודש נוכחי" : "הצג את כל החודשים"}
                  </button>
                </div>

                {/* Add Note Form */}
                <div className='add-note-form'>
                  <div className='note-type-selector'>
                    <button
                      className={`note-type-btn ${
                        newNoteType === "monthly" ? "active" : ""
                      }`}
                      onClick={() => setNewNoteType("monthly")}
                    >
                      <Calendar size={16} />
                      הערה חודשית
                    </button>
                    <button
                      className={`note-type-btn ${
                        newNoteType === "permanent" ? "active" : ""
                      }`}
                      onClick={() => setNewNoteType("permanent")}
                    >
                      <Infinity size={16} />
                      הערה קבועה
                    </button>
                  </div>
                  <div className='note-input-group'>
                    <textarea
                      className='note-textarea'
                      placeholder={
                        newNoteType === "monthly"
                          ? "הוסף הערה לחודש הנוכחי..."
                          : "הוסף הערה קבועה..."
                      }
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      rows={3}
                    />
                    <button
                      className='btn-send-note'
                      onClick={handleAddNote}
                      disabled={!newNoteText.trim()}
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>

                {/* Notes List */}
                <div className='notes-list'>
                  {loadingNotes ? (
                    <div className='notes-loading'>טוען הערות...</div>
                  ) : notes.length === 0 ? (
                    <div className='notes-empty'>
                      <StickyNote size={40} />
                      <p>אין הערות עדיין</p>
                      <span>הוסף הערה ראשונה למעלה</span>
                    </div>
                  ) : (
                    notes.map((note) => (
                      <div
                        key={note.note_id}
                        className={`note-card ${note.note_type}`}
                      >
                        <div className='note-header'>
                          <div className='note-type-badge'>
                            {note.note_type === "monthly" ? (
                              <>
                                <Calendar size={14} />
                                <span>
                                  {note.month}/{note.year}
                                </span>
                              </>
                            ) : (
                              <>
                                <Infinity size={14} />
                                <span>קבועה</span>
                              </>
                            )}
                          </div>
                          <div className='note-actions'>
                            <button
                              className='btn-edit-note'
                              onClick={() => handleEditNote(note)}
                              title='ערוך הערה'
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              className='btn-delete-note'
                              onClick={() => handleDeleteNote(note.note_id)}
                              title='מחק הערה'
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        {editingNote === note.note_id ? (
                          <div className='note-edit-form'>
                            <textarea
                              className='note-edit-textarea'
                              value={editNoteText}
                              onChange={(e) => setEditNoteText(e.target.value)}
                              autoFocus
                            />
                            <div className='note-edit-actions'>
                              <button
                                className='btn-save-note'
                                onClick={() => handleUpdateNote(note.note_id)}
                              >
                                שמור
                              </button>
                              <button
                                className='btn-cancel-note'
                                onClick={cancelEditNote}
                              >
                                ביטול
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className='note-text'>{note.note_text}</div>
                        )}
                        <div className='note-footer'>
                          <span className='note-date'>
                            {new Date(note.created_at).toLocaleDateString(
                              "he-IL",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
